<template>
  <div class="tools-page">
    <section class="hero">
      <div>
        <span class="eyebrow">纯浏览器处理 · 不上传数据</span>
        <h1>运维百宝箱</h1>
        <p>集中处理排障、接口联调和日志分析中的高频小任务。</p>
      </div>
      <div class="clock-strip">
        <div class="clock-item">
          <span>UTC</span>
          <strong>{{ utcTime }}</strong>
        </div>
        <div class="clock-item beijing">
          <span>北京时间 UTC+8</span>
          <strong>{{ beijingTime }}</strong>
        </div>
        <button class="copy-time" type="button" @click="copyText(beijingTime)">复制</button>
      </div>
    </section>

    <section class="workspace">
      <el-tabs v-model="activeTool" class="tool-tabs">
        <el-tab-pane label="Base64 / URL" name="codec">
          <div class="tool-heading">
            <div><h2>文本编解码</h2><p>支持 UTF-8 中文；内容仅在当前浏览器中处理。</p></div>
            <button class="text-button" type="button" @click="clearCodec">清空</button>
          </div>
          <div class="editor-grid">
            <label class="editor"><span>输入</span><textarea v-model="codecInput" placeholder="粘贴待处理文本"></textarea></label>
            <label class="editor"><span>结果</span><textarea v-model="codecOutput" readonly placeholder="处理结果"></textarea></label>
          </div>
          <div class="actions">
            <el-button type="primary" @click="base64Encode">Base64 编码</el-button>
            <el-button @click="base64Decode">Base64 解码</el-button>
            <el-button @click="urlEncode">URL 编码</el-button>
            <el-button @click="urlDecode">URL 解码</el-button>
            <el-button :disabled="!codecOutput" @click="copyText(codecOutput)">复制结果</el-button>
          </div>
        </el-tab-pane>

        <el-tab-pane label="时间戳" name="timestamp">
          <div class="tool-heading"><div><h2>Unix 时间戳转换</h2><p>自动识别秒和毫秒，结果同时显示 UTC 与北京时间。</p></div></div>
          <div class="timestamp-now">
            <span>当前时间戳</span>
            <strong>{{ unixSeconds }}</strong>
            <small>{{ unixMilliseconds }} ms</small>
            <button class="text-button" type="button" @click="copyText(String(unixSeconds))">复制</button>
          </div>
          <div class="field-row">
            <label><span>时间戳（秒或毫秒）</span><input v-model.trim="timestampInput" placeholder="例如 1723017600" @keyup.enter="convertTimestamp" /></label>
            <el-button type="primary" @click="convertTimestamp">转换</el-button>
          </div>
          <div v-if="timestampResult" class="result-list">
            <div><span>UTC</span><strong>{{ timestampResult.utc }}</strong></div>
            <div><span>北京时间</span><strong>{{ timestampResult.beijing }}</strong></div>
            <div><span>ISO 8601</span><strong>{{ timestampResult.iso }}</strong></div>
          </div>
          <p v-if="timestampError" class="error-text">{{ timestampError }}</p>
        </el-tab-pane>

        <el-tab-pane label="JSON" name="json">
          <div class="tool-heading">
            <div><h2>JSON 格式化与校验</h2><p>快速定位语法错误，也可压缩为单行用于请求参数。</p></div>
            <button class="text-button" type="button" @click="clearJson">清空</button>
          </div>
          <div class="editor-grid">
            <label class="editor"><span>原始 JSON</span><textarea v-model="jsonInput" placeholder='{"status":"ok"}'></textarea></label>
            <label class="editor"><span>处理结果</span><textarea v-model="jsonOutput" readonly></textarea></label>
          </div>
          <p v-if="jsonError" class="error-text">{{ jsonError }}</p>
          <div class="actions">
            <el-button type="primary" @click="formatJson">格式化</el-button>
            <el-button @click="minifyJson">压缩</el-button>
            <el-button :disabled="!jsonOutput" @click="copyText(jsonOutput)">复制结果</el-button>
          </div>
        </el-tab-pane>

        <el-tab-pane label="哈希" name="hash">
          <div class="tool-heading"><div><h2>文本摘要</h2><p>适合校验配置和短文本；大文件请使用系统的 sha256sum。</p></div></div>
          <label class="editor single"><span>输入文本</span><textarea v-model="hashInput" placeholder="输入待计算内容"></textarea></label>
          <div class="hash-results">
            <div v-for="item in hashResults" :key="item.name">
              <span>{{ item.name }}</span><code>{{ item.value || "输入内容后自动计算" }}</code>
              <button class="text-button" type="button" :disabled="!item.value" @click="copyText(item.value)">复制</button>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane label="JWT 解析" name="jwt">
          <div class="tool-heading"><div><h2>JWT 内容解析</h2><p>仅解析 Header 和 Payload，不验证签名，也不要粘贴仍有效的生产令牌。</p></div></div>
          <label class="editor single"><span>JWT</span><textarea v-model.trim="jwtInput" placeholder="eyJhbGciOi..."></textarea></label>
          <div class="actions"><el-button type="primary" @click="decodeJwt">解析</el-button><el-button @click="jwtInput = ''; jwtOutput = ''; jwtError = ''">清空</el-button></div>
          <p v-if="jwtError" class="error-text">{{ jwtError }}</p>
          <pre v-if="jwtOutput" class="code-output">{{ jwtOutput }}</pre>
        </el-tab-pane>
      </el-tabs>
    </section>
  </div>
</template>

<script>
import CryptoJS from "crypto-js";

const formatInZone = (date, timeZone) =>
  new Intl.DateTimeFormat("zh-CN", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);

const utf8ToBase64 = (value) =>
  btoa(Array.from(new TextEncoder().encode(value), (byte) => String.fromCharCode(byte)).join(""));

const base64ToUtf8 = (value) => {
  const base = value.replace(/\s/g, "").replace(/-/g, "+").replace(/_/g, "/");
  const normalized = base.padEnd(base.length + ((4 - (base.length % 4)) % 4), "=");
  const bytes = Uint8Array.from(atob(normalized), (char) => char.charCodeAt(0));
  return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
};

export default {
  name: "OpsTools",
  data() {
    const now = new Date();
    return {
      activeTool: "codec",
      now,
      clockTimer: null,
      codecInput: "",
      codecOutput: "",
      timestampInput: "",
      timestampResult: null,
      timestampError: "",
      jsonInput: "",
      jsonOutput: "",
      jsonError: "",
      hashInput: "",
      jwtInput: "",
      jwtOutput: "",
      jwtError: "",
    };
  },
  computed: {
    utcTime() { return formatInZone(this.now, "UTC"); },
    beijingTime() { return formatInZone(this.now, "Asia/Shanghai"); },
    unixSeconds() { return Math.floor(this.now.getTime() / 1000); },
    unixMilliseconds() { return this.now.getTime(); },
    hashResults() {
      if (!this.hashInput) return ["MD5", "SHA-1", "SHA-256"].map((name) => ({ name, value: "" }));
      return [
        { name: "MD5", value: CryptoJS.MD5(this.hashInput).toString() },
        { name: "SHA-1", value: CryptoJS.SHA1(this.hashInput).toString() },
        { name: "SHA-256", value: CryptoJS.SHA256(this.hashInput).toString() },
      ];
    },
  },
  mounted() {
    this.clockTimer = window.setInterval(() => { this.now = new Date(); }, 1000);
  },
  beforeUnmount() {
    window.clearInterval(this.clockTimer);
  },
  methods: {
    notifyError(message) { this.$message.error(message); },
    async copyText(value) {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        this.$message.success("已复制");
      } catch (_) {
        this.$message.error("复制失败，请手动复制");
      }
    },
    runCodec(handler) {
      try { this.codecOutput = handler(this.codecInput); }
      catch (error) { this.codecOutput = ""; this.notifyError(`处理失败：${error.message}`); }
    },
    base64Encode() { this.runCodec(utf8ToBase64); },
    base64Decode() { this.runCodec(base64ToUtf8); },
    urlEncode() { this.runCodec(encodeURIComponent); },
    urlDecode() { this.runCodec(decodeURIComponent); },
    clearCodec() { this.codecInput = ""; this.codecOutput = ""; },
    convertTimestamp() {
      this.timestampError = "";
      this.timestampResult = null;
      if (!/^-?\d+(?:\.\d+)?$/.test(this.timestampInput)) {
        this.timestampError = "请输入有效的 Unix 时间戳。";
        return;
      }
      const raw = Number(this.timestampInput);
      const milliseconds = Math.abs(raw) < 100000000000 ? raw * 1000 : raw;
      const date = new Date(milliseconds);
      if (Number.isNaN(date.getTime())) {
        this.timestampError = "时间戳超出可转换范围。";
        return;
      }
      this.timestampResult = {
        utc: formatInZone(date, "UTC"),
        beijing: formatInZone(date, "Asia/Shanghai"),
        iso: date.toISOString(),
      };
    },
    processJson(indent) {
      this.jsonError = "";
      try { this.jsonOutput = JSON.stringify(JSON.parse(this.jsonInput), null, indent); }
      catch (error) { this.jsonOutput = ""; this.jsonError = `JSON 无效：${error.message}`; }
    },
    formatJson() { this.processJson(2); },
    minifyJson() { this.processJson(0); },
    clearJson() { this.jsonInput = ""; this.jsonOutput = ""; this.jsonError = ""; },
    decodeJwt() {
      this.jwtError = "";
      this.jwtOutput = "";
      try {
        const parts = this.jwtInput.split(".");
        if (parts.length !== 3) throw new Error("JWT 应包含三个由点分隔的部分");
        const header = JSON.parse(base64ToUtf8(parts[0]));
        const payload = JSON.parse(base64ToUtf8(parts[1]));
        this.jwtOutput = JSON.stringify({ header, payload }, null, 2);
      } catch (error) { this.jwtError = `解析失败：${error.message}`; }
    },
  },
};
</script>

<style scoped>
.tools-page { min-height: calc(100vh - 64px); padding: 28px 20px 52px; background: #f4f7fb; color: #172033; }
.hero, .workspace { width: min(1180px, 100%); margin: 0 auto; box-sizing: border-box; }
.hero { display: flex; justify-content: space-between; gap: 28px; align-items: center; padding: 26px 30px; border: 1px solid #dfe7f1; border-radius: 16px; background: linear-gradient(135deg, #fff, #f3f8ff); }
.eyebrow { color: #2871d7; font-size: 13px; font-weight: 700; }
h1 { margin: 7px 0 6px; font-size: 30px; } h2 { margin: 0 0 5px; font-size: 20px; }
.hero p, .tool-heading p { margin: 0; color: #68758a; }
.clock-strip { display: flex; align-items: stretch; border: 1px solid #dce5f0; border-radius: 12px; background: #fff; overflow: hidden; }
.clock-item { min-width: 175px; padding: 12px 16px; border-right: 1px solid #e7edf4; }
.clock-item span { display: block; margin-bottom: 5px; color: #718096; font-size: 12px; }
.clock-item strong { font-size: 14px; white-space: nowrap; }
.clock-item.beijing strong { color: #1667c9; }
.copy-time, .text-button { border: 0; background: transparent; color: #276dcc; cursor: pointer; }
.copy-time { padding: 0 15px; }
.workspace { margin-top: 18px; padding: 18px 28px 30px; border: 1px solid #e1e8f0; border-radius: 16px; background: #fff; box-shadow: 0 8px 25px rgba(39, 61, 91, 0.05); }
.tool-heading { display: flex; justify-content: space-between; align-items: start; gap: 20px; margin: 7px 0 18px; }
.editor-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.editor { display: block; } .editor.single { margin-top: 12px; }
.editor > span, .field-row label > span { display: block; margin-bottom: 7px; color: #4b596d; font-size: 13px; font-weight: 600; }
textarea, input { width: 100%; box-sizing: border-box; border: 1px solid #d7e0ea; border-radius: 9px; background: #fbfcfe; color: #1f2937; font: 14px/1.55 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; outline: none; }
textarea { min-height: 210px; padding: 13px; resize: vertical; } input { height: 40px; padding: 0 12px; }
textarea:focus, input:focus { border-color: #4b8de5; box-shadow: 0 0 0 3px rgba(75, 141, 229, 0.1); }
.actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 15px; }
.timestamp-now { display: flex; align-items: center; gap: 16px; padding: 14px 16px; border-radius: 10px; background: #f3f7fc; }
.timestamp-now span, .timestamp-now small { color: #67758a; } .timestamp-now strong { font: 700 18px ui-monospace, monospace; }
.field-row { display: flex; align-items: end; gap: 12px; margin-top: 18px; }.field-row label { flex: 1; }
.result-list { display: grid; gap: 8px; margin-top: 16px; }
.result-list div { display: grid; grid-template-columns: 100px 1fr; padding: 11px 14px; border-radius: 8px; background: #f7f9fc; }
.result-list span { color: #667489; }.result-list strong { font-family: ui-monospace, monospace; }
.error-text { margin: 12px 0 0; color: #c0392b; }
.hash-results { display: grid; gap: 9px; margin-top: 16px; }
.hash-results > div { display: grid; grid-template-columns: 72px 1fr 50px; align-items: center; gap: 12px; padding: 12px 14px; border: 1px solid #e4eaf1; border-radius: 9px; }
.hash-results span { font-weight: 700; }.hash-results code { overflow-wrap: anywhere; color: #35445a; }
.code-output { padding: 16px; max-height: 360px; overflow: auto; border-radius: 9px; background: #182230; color: #dce8f7; text-align: left; }
button:disabled { cursor: not-allowed; opacity: 0.45; }
@media (max-width: 850px) { .hero { align-items: stretch; flex-direction: column; }.clock-strip { display: grid; grid-template-columns: 1fr 1fr; }.copy-time { grid-column: 1 / -1; padding: 10px; border-top: 1px solid #e7edf4; }.editor-grid { grid-template-columns: 1fr; } }
@media (max-width: 560px) { .tools-page { padding: 14px 10px 36px; }.hero, .workspace { padding: 20px 16px; }.clock-strip { grid-template-columns: 1fr; }.clock-item { border-right: 0; border-bottom: 1px solid #e7edf4; }.timestamp-now { align-items: flex-start; flex-direction: column; gap: 5px; }.result-list div { grid-template-columns: 1fr; gap: 4px; }.hash-results > div { grid-template-columns: 1fr; } }
</style>
